// ** Framework imports
import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";

// ** MUI imports
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";

// ** Components imports
import AdminNavigation from "@/components/navigation/AdminNavigation.tsx";

// ** Auth imports
import { requireRole } from "@/auth/requireRole.ts";
import { ROLE_ADMIN } from "@/constants";

// ** Store imports
import { store } from "@/store";
import { loggedOut } from "@/store/auth";
import { authApi } from "@/api/authApi.ts";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { toast } from "react-toastify";
import i18n from "@i18n/config.ts";

// ** Config imports
import { adminNavigationConfig } from "@/config/adminNavigation.ts";
import authConfig from "@/config/auth.ts";

const isAuthError = (error: FetchBaseQueryError): boolean =>
  error.status === 401 || error.status === 403;

export const Route = createFileRoute("/admin")({
  component: AdminLayoutComponent,
  beforeLoad: (args) => requireRole([ROLE_ADMIN])(args),
  // Validate the JWT server-side before rendering the admin UI.
  // This prevents stale redux-persist state from granting access
  // when the token has actually expired on the backend.
  loader: async ({ location }) => {
    // Skip server validation if no token exists (beforeLoad already handles this,
    // but acts as a defensive early return to avoid an unnecessary API call)
    if (!localStorage.getItem(authConfig.storageTokenKeyName)) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }

    const promise = store.dispatch(
      authApi.endpoints.getMe.initiate(undefined, { forceRefetch: true }),
    );
    try {
      const result = await promise;
      if (result.isError) {
        const fetchError = result.error as FetchBaseQueryError;
        // Only redirect to login on auth errors (401/403).
        // Other errors (500, network) are transient — let the UI render
        // and let RTK Query handle retries at the component level.
        if (isAuthError(fetchError)) {
          store.dispatch(loggedOut());
          toast.info(i18n.t("login.sessionExpired"));
          throw redirect({
            to: "/login",
            search: { redirect: location.href },
          });
        }
      }
    } finally {
      promise.unsubscribe();
    }
  },
  notFoundComponent: AdminNotFound,
});

function AdminNotFound() {
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
      <Typography variant="h1" color="text.primary" fontWeight={700}>
        404
      </Typography>
      <Typography variant="h6" color="text.secondary">
        Page non trouvée
      </Typography>
    </Stack>
  );
}

function AdminLayoutComponent() {
  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Box
      sx={(theme) => ({
        display: "flex",
        minHeight: "100vh",
        bgcolor: theme.palette.background.default,
      })}
    >
      <AdminNavigation />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: `calc(100% - ${adminNavigationConfig.drawerWidth}px)`,
        }}
      >
        <Outlet />
      </Box>
    </Box>
    </>
  );
}
